import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { ListingDetails } from 'app/types/listingDetails.type';
import FetchListingActions from 'app/shared/flux/actions/FetchListingActions';
import { DEFAULT } from 'app/shared/models/Filter';
import routeUtils from 'app/shared/utils/routeUtils';
import type { BedroomLabel, ComparisonRow } from './comparisonTableUtils';
import { buildComparisonRows } from './comparisonTableUtils';

const DEFAULT_SIMILAR_LISTING_TYPES = Object.keys(DEFAULT.rentalTypes).join(',');

export type SimilarListingsFetchResult = {
  currentListing: ListingDetails | null | undefined;
  rows: Array<ComparisonRow>;
  bedroomLabels: Array<BedroomLabel>;
  hasCurrentListingColumn: boolean;
};

export const useSimilarListingsFetch = (): SimilarListingsFetchResult => {
  const dispatch = useDispatch();
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const similarListings = useSelector((state: RootReduxState) => state.fetchListing?.similarListings);
  const similarListingsMaloneLotIdEncoded = useSelector(
    (state: RootReduxState) => state.fetchListing?.similarListingsMaloneLotIdEncoded,
  );
  const similarListingsUnit = useSelector((state: RootReduxState) => state.fetchListing?.similarListingsUnit);
  const similarListingsSource = useSelector((state: RootReduxState) => state.fetchListing?.similarListingsSource);
  const similarListingsLoadingKey = useSelector(
    (state: RootReduxState) => state.fetchListing?.similarListingsLoadingKey,
  );
  const fetchAttemptedForRef = useRef<string | null>(null);

  const urlMaloneLotIdEncoded =
    typeof window === 'undefined' ? undefined : routeUtils.getMaloneLotIdEncodedFromUrl(window.location.pathname);
  const maloneLotIdEncoded = currentListing?.maloneLotIdEncoded || urlMaloneLotIdEncoded;
  const unit = currentListing?.unit;
  const similarListingsMatchCurrentListing =
    similarListingsSource === 'listing' &&
    similarListingsMaloneLotIdEncoded === maloneLotIdEncoded &&
    (similarListingsUnit ?? null) === (unit ?? null);

  useEffect(() => {
    if (!maloneLotIdEncoded) return;

    const requestKey = `${maloneLotIdEncoded}:${unit ?? ''}`;

    if (similarListingsLoadingKey === requestKey) return;

    const hasStaleMismatchedData = !similarListingsMatchCurrentListing && similarListings && similarListings.length > 0;
    if (fetchAttemptedForRef.current === requestKey && !hasStaleMismatchedData) return;
    if (similarListingsMatchCurrentListing && similarListings && similarListings.length > 0) return;
    fetchAttemptedForRef.current = requestKey;

    dispatch(
      FetchListingActions.fetchSimilarListings({
        maloneLotIdEncoded,
        unit,
        listingTypes: DEFAULT_SIMILAR_LISTING_TYPES,
        limit: 8,
      }),
    );
  }, [
    dispatch,
    maloneLotIdEncoded,
    unit,
    similarListings,
    similarListingsMatchCurrentListing,
    similarListingsLoadingKey,
  ]);

  const { rows, bedroomLabels, hasCurrentListingColumn } = useMemo(() => {
    if (!currentListing || !similarListingsMatchCurrentListing || !similarListings || similarListings.length === 0) {
      return { rows: [], bedroomLabels: [], hasCurrentListingColumn: false };
    }
    return buildComparisonRows(currentListing, similarListings);
  }, [currentListing, similarListings, similarListingsMatchCurrentListing]);

  return { currentListing, rows, bedroomLabels, hasCurrentListingColumn };
};
